// FBDatePicker.tsx

import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Observer } from 'mobx-react';
import moment, { Moment } from 'moment';
import React from 'react';
import { FBInput } from '..';
import { ReactComponent as DatePickerIcon } from '../../../assets/images/formBuilder/DatePickerIcon.svg';
import { MomentFormats, useDate } from '../../../common/utils/date';
import { FBFontStyle } from '../styles/FBFont.styles';
import { FBTextFieldStyles } from '../styles/FBTextField.styles';
import { FBDatePickerProps } from './FBDatePicker.types';
import { withDatePicker } from './FBDatePicker.wrap';

const DataPickerInput: React.FC<FBDatePickerProps> = ({
  datePickerState,
  onChange,
  onDatePickerClick,
  onDatePickerClose,
  onDatePickerClear,
  disablePastDates,
  ...props
}) => {
  const classes = FBTextFieldStyles();
  const { timezone } = useDate();

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Observer>
        {() => (
          <DatePicker
            value={datePickerState?.date ? moment.tz(datePickerState.date, timezone) : null}
            defaultCalendarMonth={moment().tz(timezone)}
            onChange={(newValue: Moment | null) => {
              const formatted = newValue?.format(MomentFormats.MonthDateYearTwoDigit) ?? '';
              onChange?.(newValue, formatted);
            }}
            onOpen={() => onDatePickerClick?.()}
            onClose={() => onDatePickerClose?.()}
            disablePast={disablePastDates}
            disabled={props.disabled}
            inputFormat={MomentFormats.MonthDateYearTwoDigit}
            components={{
              OpenPickerIcon: () => <DatePickerIcon data-cy="datepicker-icon" />,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name={props.name}
                fullWidth
                variant="outlined"
                disabled={props.disabled}
                className={classes.textfield}
                inputProps={{
                  ...params.inputProps,
                  disabled: true,
                  placeholder: '',
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      {datePickerState?.date && (
                        <IconButton
                          onClick={onDatePickerClear}
                          size="small"
                          disabled={props.disabled}
                          data-cy="date-clear"
                          aria-label="clear date"
                        >
                          <FontAwesomeIcon icon={regular('xmark')} />
                        </IconButton>
                      )}
                      {params.InputProps?.endAdornment}
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        )}
      </Observer>
    </LocalizationProvider>
  );
};

const FBDatePicker: React.FunctionComponent<FBDatePickerProps> = (props) => (
  <FBInput {...props} withState type="datepicker" labelProps={FBFontStyle.labelProps}>
    <DataPickerInput {...props} />
  </FBInput>
);

export default withDatePicker(FBDatePicker);
